// Job 35: network tariffs as dated contracts.
//
// The pure decisions that used to be implicit in three different stores:
//   * which code a site is on for a given date  → `currentNetworkCode`
//   * which rate period prices a given date     → `ratePeriodForDate`
//
// Both are here rather than in the service because they decide what a bill is
// priced from. The service writes the answer back to
// `site.bv_extn.network_tariff_code` and `site.network_codes`, which the NEM
// pipeline and the Salesforce sync still read — so getting this wrong moves
// money, and it is tested on its own.
//
// Dates are ISO `YYYY-MM-DD` throughout and compared as strings: correct for ISO
// and free of the timezone shift `new Date('2026-07-01')` introduces.

import type { BvNetworkContract, BvNetworkRate } from '../types/billValidationTypes';

/** Inclusive on both ends: a window starting or ending on `date` covers it. */
export const windowCoversDate = (
  from: string | null | undefined,
  to: string | null | undefined,
  date: string,
): boolean => {
  if (from && from > date) return false;
  if (to && to < date) return false;
  return true;
};

/**
 * The tariff code the site is on as of `date`.
 *
 * Prefers the narrowest matching window, so a code entered with an explicit
 * window beats one left open at both ends. That matters on the sites migration
 * 101 backfilled from `bv_extn.network_tariff_code`, which have no dates at all:
 * the moment someone records a real window, the dated code wins without anyone
 * having to delete the undated one first.
 *
 * Ties break on the later start, then on the code, so the answer is stable
 * rather than dependent on row order.
 */
export const currentNetworkCode = (
  contracts: BvNetworkContract[],
  date: string,
): string | null => {
  const covering = contracts.filter(
    (c) => !!c.tariff_code?.trim() && windowCoversDate(c.valid_from, c.valid_to, date),
  );
  if (covering.length === 0) return null;

  const score = (c: BvNetworkContract) => (c.valid_from ? 1 : 0) + (c.valid_to ? 1 : 0);
  const best = covering.reduce((a, b) => {
    const sa = score(a);
    const sb = score(b);
    if (sa !== sb) return sa > sb ? a : b;
    const fa = a.valid_from ?? '';
    const fb = b.valid_from ?? '';
    if (fa !== fb) return fa > fb ? a : b;
    return a.tariff_code.localeCompare(b.tariff_code) <= 0 ? a : b;
  });
  return best.tariff_code.trim();
};

/**
 * Every code assigned to the site, deduplicated and in window order.
 *
 * `site.network_codes` is still written from this — the sites-list RPC, the
 * export and the SiteFormPage all read that column — so removing a contract has
 * to remove its code from the array, and adding one has to add it.
 */
export const assignedCodesFromContracts = (contracts: BvNetworkContract[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  const ordered = contracts.slice().sort((a, b) => {
    const fa = a.valid_from ?? '';
    const fb = b.valid_from ?? '';
    // Undated last: an undated assignment is the one nobody has dated yet, not
    // the site's earliest tariff.
    if (!fa && fb) return 1;
    if (fa && !fb) return -1;
    return fa.localeCompare(fb);
  });
  for (const c of ordered) {
    const code = c.tariff_code?.trim();
    if (!code) continue;
    const key = code.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(code);
  }
  return out;
};

/**
 * The rate period that prices `date` on one contract.
 *
 * A smooth contract holds a single period spanning the whole window and its
 * period dates may be blank — an undated period is therefore treated as
 * covering the contract, not as covering nothing. Falls back to the only period
 * when exactly one exists, which is what makes a smooth contract work without
 * anyone typing its dates twice.
 */
export const ratePeriodForDate = (
  rates: BvNetworkRate[] | undefined,
  date: string,
): BvNetworkRate | null => {
  const periods = rates ?? [];
  if (periods.length === 0) return null;

  const covering = periods.filter((r) => windowCoversDate(r.period_start, r.period_end, date));
  if (covering.length === 1) return covering[0];
  if (covering.length > 1) {
    // Prefer a period with real dates over a catch-all left blank.
    const dated = covering.filter((r) => r.period_start || r.period_end);
    const pool = dated.length > 0 ? dated : covering;
    return pool.reduce((a, b) => ((a.period_start ?? '') > (b.period_start ?? '') ? a : b));
  }
  return periods.length === 1 ? periods[0] : null;
};
